import {
  DEFAULT_STACK_TOWER_CONFIG,
  createInitialTowerState,
  currentMovingBlockFootprint,
  deriveRunResult,
  footprintWidth,
  multiplierForClimb,
  placeBlock,
  resetTowerKeepingScore,
  zoneForHeight,
  type StackTowerConfig,
  type StackTowerRunResult,
  type StackTowerZone,
  type TowerRunState,
} from '@ranked-waiting/stack-tower-core';
import { mountCanvasSurface } from './stack-tower-canvas';
import { bindStackTowerInput } from './stack-tower-input';
import { cameraOffsetForTowerTop, dampTowardTarget, worldToScreenY } from './stack-tower-camera';
import { drawStackTowerFrame, type SettledBlockView } from './stack-tower-render';
import { spawnMissPiece, spawnOverhangPiece, stepFallingPiece, type FallingPiece } from './stack-tower-physics';
import { playStackTowerHit, playStackTowerMiss, playStackTowerNearMiss, playStackTowerPerfect } from './stack-tower-audio';

export interface StackTowerEngineOptions {
  config?: StackTowerConfig;
  runId: string;
  cameraSmoothingSec?: number;
  /**
   * Solo 60 Second (spec §98), when set: the run is a fixed-duration window
   * instead of play-until-miss — a miss soft-resets the tower and keeps
   * score (like head-to-head's timed mode, spec §103, owner's call for §98
   * too) rather than ending the run; the run instead ends when this many ms
   * have elapsed. Omit for unchanged Solo Endless behaviour (a miss ends
   * the run, no clock).
   */
  durationMs?: number;
  onScoreChanged?: (score: number) => void;
  onMultiplierChanged?: (multiplier: number) => void;
  onPerfectStreakChanged?: (streak: number) => void;
  onZoneChanged?: (zone: StackTowerZone) => void;
  onGameOver?: (result: StackTowerRunResult) => void;
}

export interface StackTowerEngine {
  destroy(): void;
  /** Freezes the loop (no input, no simulation advance) — spec §66/§83. Safe to call repeatedly; a second pause while already paused is a no-op. */
  pause(): void;
  /** Resumes from `pause()`. Shifts the moving block's spawn time forward by however long the pause lasted, so it doesn't visually teleport to where it "would have" swept to while frozen. */
  resume(): void;
  isPaused(): boolean;
}

/** Clamps a tab-backgrounding/devtools-breakpoint stall so the camera can't teleport on the next frame. */
const MAX_DELTA_MS = 100;

const MOVING_BLOCK_COLOR = '#f3f1e9'; // --colour-paper — matches the moving block, so a cut sliver keeps its colour as it falls
const SHAKE_DURATION_MS = 220;
const HIT_SHAKE_UNITS = 4;
const PERFECT_SHAKE_UNITS = 6;
const MISS_SHAKE_UNITS = 13;
const NEAR_MISS_SHAKE_UNITS = 9; // between a normal hit and a full miss — noticeable, not alarming
/** A surviving sliver narrower than this fraction of the starting width reads as "barely made it" (spec §12), not just an off-centre hit. */
const NEAR_MISS_WIDTH_FRACTION = 0.12;
/** Cull a falling piece once its top edge is this far below the viewport — comfortably off-screen, so it's gone before it could ever reappear. */
const PIECE_CULL_MARGIN_PX = 140;

/**
 * Owns the whole live game loop: canvas surface, input, the pure
 * `TowerRunState`, the camera, falling-debris physics, and drawing. Returns a
 * `destroy()` — the rAF cancellation is what actually matters here (in a
 * single-threaded event loop, calling it before a scheduled frame fires
 * reliably prevents that callback from ever running, so there's no race to
 * guard beyond it), and `apps/web/src/views/stack-tower.ts`'s `RouteHandler`
 * returns this closure directly so navigating away is safe by construction —
 * see router.ts's `resolve()`, which always calls the previous route's
 * teardown before clearing the container.
 */
export function mountStackTowerEngine(container: HTMLElement, options: StackTowerEngineOptions): StackTowerEngine {
  const config = options.config ?? DEFAULT_STACK_TOWER_CONFIG;
  const smoothingSec = options.cameraSmoothingSec ?? 0.18;
  // Read once at mount, not reactively — matches how every other reduced-motion
  // consumer in this app treats it (a per-page/per-mount setting, not a live toggle
  // mid-session). Satisfies spec §90 ("allow optional reduction... without
  // affecting gameplay") using the preference hook this project already has
  // (session.ts's applyPreferences sets documentElement.dataset.reducedMotion).
  // Scope: suppress camera shake and the perfect-hit flash/squash; the tower,
  // camera follow and falling debris still animate (they carry information, not
  // just flourish).
  const reducedMotion = document.documentElement.dataset.reducedMotion === 'true';

  const surface = mountCanvasSurface(container);
  const startedAtMs = performance.now();

  let state: TowerRunState = createInitialTowerState(config, options.runId, startedAtMs);
  // The pure package only tracks `topFootprint` (what game logic needs);
  // the full settled stack is a rendering-only concern, kept here. Unbounded
  // for now — culling blocks far below the camera (spec §6 already
  // anticipates this) is a "revisit if profiling shows it" concern, the
  // same call already made for falling-piece pooling, not a Phase 2 blocker.
  const settledBlocks: SettledBlockView[] = [{ footprint: state.topFootprint, worldY: 0 }];
  const fallingPieces: FallingPiece[] = [];
  let cameraOffset = 0;
  let lastFrameMs = startedAtMs;
  let rafId: number | null = null;
  let destroyed = false;
  let paused = false;
  let pausedAtMs = 0;
  let finished = false;
  // Timed mode only (Solo 60 Second). `performance.now()`-timeline deadline,
  // plus a setTimeout anchored to it — the same backgrounded-tab-robust
  // pairing head-to-head's own pane already established (a backgrounded
  // tab's rAF is throttled/suspended, but setTimeout keeps firing).
  let deadlinePerfMs = options.durationMs !== undefined ? startedAtMs + options.durationMs : null;
  let deadlineTimeoutId: number | undefined;

  // Impact squash/flash on the just-placed block, and a decaying camera shake.
  let impactWorldY = 0;
  let impactStartedMs = -Infinity;
  let impactPerfect = false;
  let impactStreak = 0;
  let impactNearMiss = false;
  let shakeMag = 0;
  let shakeElapsedMs = 0;
  let lastZoneId = zoneForHeight(state.climbBlocks).id;
  let lastMultiplier = multiplierForClimb(state.climbBlocks, config);

  function triggerShake(units: number) {
    if (reducedMotion) return;
    shakeMag = units;
    shakeElapsedMs = 0;
  }

  function place() {
    if (destroyed || paused || finished || state.phase !== 'playing') return;
    const nowMs = performance.now();
    const result = placeBlock(state, nowMs, config);
    if (!result.ok) return;
    const previousStreak = state.perfectStreak;
    const outcome = result.outcome;
    state = result.state;

    if (outcome.kind === 'hit') {
      // The placed block's bottom edge sits at (new climb height) *
      // blockHeight — the same row the moving block was hovering on before
      // placement. Height, not score: score can now climb faster than the
      // tower once the multiplier kicks in (rules.ts), so it's no longer a
      // safe stand-in for "how tall is the tower".
      const worldY = state.climbBlocks * config.blockHeight;
      settledBlocks.push({ footprint: state.topFootprint, worldY });
      // Spec §12: a valid hit that barely survived — never changes that it
      // counted as a hit, only how it's presented.
      const nearMiss = !outcome.perfect && footprintWidth(outcome.newFootprint) < config.startingBlockWidth * NEAR_MISS_WIDTH_FRACTION;
      if (outcome.overhang) {
        fallingPieces.push(spawnOverhangPiece(outcome.overhang, worldY, config.blockHeight, MOVING_BLOCK_COLOR, nearMiss));
      }
      if (!reducedMotion) {
        impactWorldY = worldY;
        impactStartedMs = nowMs;
        impactPerfect = outcome.perfect;
        impactStreak = state.perfectStreak;
        impactNearMiss = nearMiss;
      }
      triggerShake(outcome.perfect ? PERFECT_SHAKE_UNITS : nearMiss ? NEAR_MISS_SHAKE_UNITS : HIT_SHAKE_UNITS);
      if (outcome.perfect) playStackTowerPerfect(state.perfectStreak);
      else if (nearMiss) playStackTowerNearMiss();
      else playStackTowerHit();
      options.onScoreChanged?.(state.score);
      const multiplier = multiplierForClimb(state.climbBlocks, config);
      if (multiplier !== lastMultiplier) {
        lastMultiplier = multiplier;
        options.onMultiplierChanged?.(multiplier);
      }
      if (state.perfectStreak !== previousStreak) options.onPerfectStreakChanged?.(state.perfectStreak);
      const zone = zoneForHeight(state.climbBlocks);
      if (zone.id !== lastZoneId) {
        lastZoneId = zone.id;
        options.onZoneChanged?.(zone);
      }
    } else {
      // Miss: the whole moving block toppled off. It shared the row the moving
      // preview was on — one above the current (unincremented) top.
      const worldY = (state.climbBlocks + 1) * config.blockHeight;
      fallingPieces.push(
        spawnMissPiece(outcome.movingBlock, worldY, config.blockHeight, outcome.movingBlock.min < state.topFootprint.min ? -1 : 1, MOVING_BLOCK_COLOR),
      );
      triggerShake(MISS_SHAKE_UNITS);
      playStackTowerMiss();
      // Solo 60 Second (spec §98, owner's call): a miss costs the tower and
      // the climb multiplier, not the run — the same soft-reset head-to-head
      // uses (spec §103). `placeBlock` already set phase to game_over above;
      // undo that here rather than teaching the core package two Solo modes.
      if (options.durationMs !== undefined) {
        const scoreBeforeReset = state.score;
        state = resetTowerKeepingScore(state, config, nowMs);
        settledBlocks.length = 1; // back to just the base block — matches resetTowerKeepingScore's own geometry reset
        // The early-miss penalty (rules.ts) can change score on a miss, not
        // just a hit — without this, the HUD would show the stale
        // pre-penalty number until the next successful placement.
        if (state.score !== scoreBeforeReset) options.onScoreChanged?.(state.score);
      }
    }

    if (state.phase === 'game_over') {
      finish();
    }
  }

  function finish() {
    if (finished) return;
    finished = true;
    window.clearTimeout(deadlineTimeoutId);
    options.onGameOver?.(deriveRunResult(state, config, startedAtMs, performance.now()));
  }

  const unbindInput = bindStackTowerInput(surface.canvas, place);

  // Timed mode only: the setTimeout keeps firing on a backgrounded tab
  // (unlike rAF), so a player who alt-tabs near the end still gets a real
  // final result instead of whatever the last-drawn frame happened to show.
  if (deadlinePerfMs !== null) {
    deadlineTimeoutId = window.setTimeout(finish, Math.max(0, deadlinePerfMs - performance.now()));
  }

  function frame(nowMs: number) {
    if (destroyed) return;
    rafId = requestAnimationFrame(frame);
    const deltaMs = Math.min(nowMs - lastFrameMs, MAX_DELTA_MS);
    lastFrameMs = nowMs;

    if (!finished && deadlinePerfMs !== null && nowMs >= deadlinePerfMs) finish();

    const { width, height } = surface.size();
    // The moving block hovers one row above the current top; the placed block
    // will settle at exactly this Y, so preview and placement line up with no
    // visual jump. The camera frames this row (where the action is).
    const movingWorldY = (state.climbBlocks + 1) * config.blockHeight;
    const targetCameraOffset = cameraOffsetForTowerTop(movingWorldY, height);
    cameraOffset = reducedMotion ? targetCameraOffset : dampTowardTarget(cameraOffset, targetCameraOffset, deltaMs, smoothingSec);

    // Advance and cull falling debris.
    for (let i = fallingPieces.length - 1; i >= 0; i -= 1) {
      const piece = fallingPieces[i];
      stepFallingPiece(piece, deltaMs);
      const screenTop = worldToScreenY(piece.cy, cameraOffset, height) - piece.halfHeight;
      if (screenTop > height + PIECE_CULL_MARGIN_PX) fallingPieces.splice(i, 1);
    }

    // Decay the camera shake toward zero.
    let shakeX = 0;
    let shakeY = 0;
    if (shakeMag > 0) {
      shakeElapsedMs += deltaMs;
      if (shakeElapsedMs >= SHAKE_DURATION_MS) {
        shakeMag = 0;
      } else {
        const amp = shakeMag * (1 - shakeElapsedMs / SHAKE_DURATION_MS);
        shakeX = (Math.random() * 2 - 1) * amp * 0.5;
        shakeY = (Math.random() * 2 - 1) * amp;
      }
    }

    const impactAgeMs = nowMs - impactStartedMs;
    const movingFootprint = finished ? null : currentMovingBlockFootprint(state, nowMs, config);
    drawStackTowerFrame(surface.ctx, {
      width,
      height,
      trackWidth: config.trackWidth,
      blockHeight: config.blockHeight,
      climbHeight: state.climbBlocks,
      cameraOffset,
      shakeX,
      shakeY,
      settledBlocks,
      movingBlock: movingFootprint ? { footprint: movingFootprint, worldY: movingWorldY } : null,
      fallingPieces,
      impact: Number.isFinite(impactStartedMs)
        ? { worldY: impactWorldY, ageMs: impactAgeMs, perfect: impactPerfect, streak: impactStreak, nearMiss: impactNearMiss }
        : null,
    });
  }
  rafId = requestAnimationFrame(frame);

  function pause() {
    if (destroyed || paused || finished || state.phase !== 'playing') return;
    paused = true;
    pausedAtMs = performance.now();
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    // The 60s clock (if any) doesn't run while paused either — cleared here,
    // rescheduled in resume() for whatever time was left.
    window.clearTimeout(deadlineTimeoutId);
  }

  function resume() {
    if (destroyed || !paused) return;
    paused = false;
    const pauseDurationMs = performance.now() - pausedAtMs;
    // The moving block's position is a pure function of elapsed time since
    // spawn (computeMovingFootprint / currentMovingBlockFootprint) — with no
    // clamp of its own, unlike the per-frame deltaMs below. Shift the spawn
    // time forward by however long the pause lasted so the block resumes
    // exactly where it visually froze, instead of jumping to wherever it
    // "would have" swept to while the loop wasn't running.
    state = { ...state, spawnedAtMs: state.spawnedAtMs + pauseDurationMs };
    lastFrameMs = performance.now();
    if (deadlinePerfMs !== null) {
      deadlinePerfMs += pauseDurationMs;
      deadlineTimeoutId = window.setTimeout(finish, Math.max(0, deadlinePerfMs - performance.now()));
    }
    rafId = requestAnimationFrame(frame);
  }

  return {
    destroy() {
      destroyed = true;
      window.clearTimeout(deadlineTimeoutId);
      if (rafId !== null) cancelAnimationFrame(rafId);
      unbindInput();
      surface.destroy();
    },
    pause,
    resume,
    isPaused: () => paused,
  };
}
